/**
 * PrivacyLegalSection — AI consent, privacy policy, terms of service
 * Merges the former AIConsentSection + legal links from SupportSection.
 */

import React, { useState } from 'react';
import { Linking } from 'react-native';
import {
  VStack,
  HStack,
  Box,
  Text,
  Pressable,
  Button,
  ButtonText,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';
import { useDisplayTheme } from '../../hooks/useDisplayTheme';
import { FS_XS, FS_SM, FS_MD } from '../../theme';
import { SectionCard } from '../ui';
import { getPrivacyPolicyUrl, getTermsOfServiceUrl } from '../../config/legalUrls';
import { useAIConsent, AI_PROVIDERS, translateProvider } from '../../hooks/useAIConsent';
import { ConfirmDialog } from '../ui/ConfirmDialog';

export const PrivacyLegalSection = React.memo(function PrivacyLegalSection() {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();

  const {
    hasConsent: hasAIConsent,
    consentData,
    revokeConsent,
    isLoading: isConsentLoading,
  } = useAIConsent();

  const [showWithdrawConsentDialog, setShowWithdrawConsentDialog] = useState(false);

  return (
    <>
      <SectionCard testID="profile-section-privacy-legal" title={t('profile.privacyLegal.title')}>
        {/* AI Consent */}
        <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
          {t('profile.aiConsent.description')}
        </Text>

        {hasAIConsent ? (
          <Box
            p="$4"
            rounded="$lg"
            style={{ backgroundColor: theme.successBg, borderWidth: 1, borderColor: theme.success }}
          >
            <VStack space="sm">
              <HStack justifyContent="space-between" alignItems="center">
                <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: theme.success, ...ffs }}>
                  {t('profile.aiConsent.granted')}
                </Text>
                <Text allowFontScaling={false} style={{ fontSize: FS_XS * fontScale, color: theme.textSecondary, ...ffs }}>
                  {consentData?.grantedAt
                    ? new Date(consentData.grantedAt).toLocaleDateString()
                    : ''}
                </Text>
              </HStack>
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('profile.aiConsent.dataSharedWith')}
              </Text>
              {/* Driven by AI_PROVIDERS, not a hand-written list: this summary is a
                  disclosure surface too, and a hardcoded copy silently omitted
                  OpenAI once already. */}
              {AI_PROVIDERS.map((providerId) => (
                <Text
                  key={providerId}
                  allowFontScaling={false}
                  style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}
                >
                  {"\u2022"} {translateProvider(t, providerId).name}
                </Text>
              ))}
            </VStack>
          </Box>
        ) : (
          <Box
            p="$4"
            rounded="$lg"
            style={{ backgroundColor: theme.warningBg, borderWidth: 1, borderColor: theme.warning }}
          >
            <VStack space="sm">
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: 'bold', color: theme.warning, ...ffs }}>
                {t('profile.aiConsent.notGranted')}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.textSecondary, ...ffs }}>
                {t('profile.aiConsent.notGrantedDescription')}
              </Text>
            </VStack>
          </Box>
        )}

        {hasAIConsent && (
          <Button
            testID="profile-button-revoke-consent"
            accessibilityLabel={t('profile.aiConsent.withdrawButton')}
            size="sm"
            variant="outline"
            style={{ borderColor: theme.warning }}
            onPress={() => setShowWithdrawConsentDialog(true)}
            isDisabled={isConsentLoading}
          >
            <ButtonText allowFontScaling={false} style={{ fontSize: FS_SM * fontScale, color: theme.warning, ...ffs }}>{t('profile.aiConsent.withdrawButton')}</ButtonText>
          </Button>
        )}

        {/* Legal links */}
        <VStack space="sm">
          <Pressable
            testID="profile-button-privacy-policy"
            accessibilityLabel={t('profile.support.privacyPolicyAccessibility')}
            accessibilityRole="link"
            onPress={() => Linking.openURL(getPrivacyPolicyUrl())}
          >
            <HStack
              style={{ backgroundColor: theme.inputBg }}
              p="$4"
              rounded="$lg"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
                {t('profile.support.privacyPolicy')}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}>
                {t('common.view')}
              </Text>
            </HStack>
          </Pressable>

          <Pressable
            testID="profile-button-terms"
            accessibilityLabel={t('profile.support.termsOfServiceAccessibility')}
            accessibilityRole="link"
            onPress={() => Linking.openURL(getTermsOfServiceUrl())}
          >
            <HStack
              style={{ backgroundColor: theme.inputBg }}
              p="$4"
              rounded="$lg"
              justifyContent="space-between"
              alignItems="center"
            >
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, fontWeight: '600', color: theme.text, ...ffs }}>
                {t('profile.support.termsOfService')}
              </Text>
              <Text allowFontScaling={false} style={{ fontSize: FS_MD * fontScale, color: theme.primary, fontWeight: '600', ...ffs }}>
                {t('common.view')}
              </Text>
            </HStack>
          </Pressable>
        </VStack>
      </SectionCard>

      <ConfirmDialog
        visible={showWithdrawConsentDialog}
        title={t('profile.aiConsent.withdrawTitle')}
        message={t('profile.aiConsent.withdrawMessage')}
        confirmLabel={t('profile.aiConsent.withdrawButton')}
        cancelLabel={t('common.cancel')}
        destructive
        onConfirm={() => {
          setShowWithdrawConsentDialog(false);
          revokeConsent();
        }}
        onCancel={() => setShowWithdrawConsentDialog(false)}
      />
    </>
  );
});
